// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { useNavigate } from "react-router-dom"; // TODO: useRouter → useNavigate (nav(x) → nav(x), router.replace(x) → nav(x, {replace:true}))
import { useTranslations } from "next-intl";
import { Bell, Check } from "lucide-react";
import { Button, cn, EmptyState, Skeleton } from "@chotca/ui";
import type { Notification } from "@chotca/config";
import { ErrorState } from "@/components/error-state";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "@chotca/api-client";
import { NotificationsAllDialog } from "./notifications-all-dialog";

type Align = "right" | "bottom";
type Variant = "header" | "rail";

export function NotificationsPopover({
  align = "bottom",
  variant = "header",
}: {
  align?: Align;
  variant?: Variant;
}) {
  const t = useTranslations("notifications");
  const list = useNotifications();
  const markAll = useMarkAllNotificationsRead();

  const [open, setOpen] = React.useState(false);
  const [allOpen, setAllOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items = list.data?.data.items ?? [];
  const unreadCount = list.data?.data.unreadCount ?? 0;

  return (
    <>
      <div ref={wrapRef} className="relative">
        <button
          type="button"
          title={t("title")}
          aria-label={t("title")}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "relative flex items-center justify-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember/40",
            variant === "rail"
              ? cn(
                  "h-12 w-12 rounded-full duration-200",
                  open
                    ? "bg-lime text-ink shadow-sm ring-2 ring-lime/35"
                    : "text-ash hover:bg-mist hover:text-ink active:scale-95",
                )
              : cn(
                  "h-11 w-11 rounded-full bg-paper text-ink ring-1 ring-smoke hover:bg-mist",
                  open && "bg-mist ring-ink",
                ),
          )}
        >
          <Bell
            size={variant === "rail" ? 22 : 20}
            strokeWidth={variant === "rail" ? 1.5 : 1.7}
          />
          {unreadCount > 0 ? (
            <span
              aria-label={t("unreadBadge", { count: unreadCount })}
              className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-ember px-1 text-[10px] font-bold leading-none text-paper ring-2 ring-paper"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          ) : null}
        </button>

        {open ? (
          <div
            role="dialog"
            aria-label={t("title")}
            className={cn(
              "z-50 flex flex-col overflow-hidden rounded-lg border border-smoke bg-paper shadow-[0_20px_60px_rgba(15,15,15,0.18)]",
              "animate-in fade-in zoom-in-95 duration-150",
              // Mobile: fixed full-width panel anchored below header
              "fixed inset-x-3 top-18 max-h-[calc(100dvh-5.5rem)]",
              // Desktop: absolute popover
              "md:absolute md:inset-x-auto md:top-auto md:max-h-none md:w-88 md:max-w-[calc(100vw-2rem)]",
              align === "bottom"
                ? "md:top-full md:mt-3 md:right-0"
                : "md:left-full md:bottom-0 md:ml-3",
            )}
          >
            <div className="flex items-center justify-between gap-3 border-b border-smoke px-4 py-3">
              <p className="text-title-sm font-extrabold text-ink">
                {t("title")}
              </p>
              {unreadCount > 0 ? (
                <span className="text-body-sm text-ash">
                  {t("unreadBadge", { count: unreadCount })}
                </span>
              ) : null}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {list.isLoading ? (
                <div className="p-4">
                  <Skeleton className="h-24 w-full" />
                </div>
              ) : list.isError ? (
                <div className="p-4">
                  <ErrorState onRetry={() => list.refetch()} />
                </div>
              ) : items.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    icon={<Bell className="h-7 w-7" />}
                    title={t("empty")}
                  />
                </div>
              ) : (
                <ul className="divide-y divide-smoke">
                  {items.map((n) => (
                    <NotificationItem
                      key={n.id}
                      n={n}
                      onNavigate={() => setOpen(false)}
                    />
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-smoke px-3 py-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setOpen(false);
                  setAllOpen(true);
                }}
              >
                {t("viewAll")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={unreadCount === 0 || markAll.isPending}
                onClick={() => markAll.mutate()}
              >
                {t("markAllRead")}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <NotificationsAllDialog
        open={allOpen}
        onClose={() => setAllOpen(false)}
      />
    </>
  );
}

// Backend stores deeplinks like "/employer/postings/123" so they round-trip
// to mobile push + email. The employer SPA is mounted at the subdomain root,
// so strip the legacy "/employer" (or "/admin") prefix before navigating.
function spaPath(actionUrl: string): string {
  const stripped = actionUrl.replace(/^\/(employer|admin)(?=\/|$)/, "");
  return stripped || "/dashboard";
}

export function NotificationItem({
  n,
  onNavigate,
}: {
  n: Notification;
  onNavigate?: () => void;
}) {
  const t = useTranslations("notifications");
  const nav = useNavigate();
  const markOne = useMarkNotificationRead();
  const isRead = !!n.readAt;
  const hasAction = !!n.actionUrl;
  const interactive = hasAction || !isRead;

  const handleNavigate = () => {
    if (!isRead) void markOne.mutateAsync(n.id).catch(() => undefined);
    onNavigate?.();
    if (n.actionUrl) nav(spaPath(n.actionUrl));
  };

  const handleMarkRead = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (isRead || markOne.isPending) return;
    void markOne.mutateAsync(n.id).catch(() => undefined);
  };

  return (
    <li
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? handleNavigate : undefined}
      onKeyDown={(e) => {
        if (!interactive) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleNavigate();
        }
      }}
      className={cn(
        "group relative px-4 py-3 transition-colors",
        isRead
          ? hasAction
            ? "cursor-pointer hover:bg-mist"
            : ""
          : "cursor-pointer bg-lime/5 hover:bg-lime/10",
      )}
    >
      <div className="flex items-start gap-3">
        {!isRead ? (
          <span
            aria-hidden
            className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-lime"
          />
        ) : (
          <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0" />
        )}
        <div className="min-w-0 flex-1 pr-8">
          <p className="text-body-sm font-semibold text-ink">{n.title}</p>
          <p className="line-clamp-2 text-body-sm text-ash">{n.body}</p>
          <p className="mt-1 text-[11px] text-ash">
            {new Date(n.createdAt).toLocaleString("vi-VN")}
          </p>
        </div>
      </div>
      {!isRead ? (
        <button
          type="button"
          aria-label={t("markRead")}
          title={t("markRead")}
          onClick={handleMarkRead}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleMarkRead(e);
          }}
          disabled={markOne.isPending}
          className={cn(
            "absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-paper text-ink ring-1 ring-smoke transition-opacity hover:bg-lime hover:ring-lime",
            "opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <Check size={14} strokeWidth={2.2} />
        </button>
      ) : null}
    </li>
  );
}
